import React, { useState, useEffect } from 'react';
import { Table, Button, Select, Spin, message } from 'antd';
import { FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import ScoreDetailModal from './ScoreDetailModal';
import { fetchExamScoreReport, exportExamScoreReport } from '../../services/reportService';
import examSessionService from '../../services/examSessionService';
import axios from 'axios';

const ExamScoreReportPage: React.FC = () => {
  // State cho filter, data, loading, modal
  const [filters, setFilters] = useState<any>({});
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [examSessions, setExamSessions] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loadingFilter, setLoadingFilter] = useState(false);

  useEffect(() => {
    // Lấy danh sách kỳ thi
    examSessionService.getExamSessions().then(res => setExamSessions(res.data || []));
  }, []);

  // Khi chọn kỳ thi, fetch lại lớp và môn học theo kỳ thi
  useEffect(() => {
    if (!filters.examSessionId) {
      setClasses([]);
      setSubjects([]);
      setFilters((prev: any) => ({ ...prev, classId: undefined, subjectId: undefined }));
      return;
    }
    setLoadingFilter(true);
    axios.get(`/api/v1/exam-session/${filters.examSessionId}/classes`).then(res => {
      setClasses(res.data.data || []);
      setFilters((prev: any) => ({ ...prev, classId: undefined }));
    });
    axios.get(`/api/v1/exam-session/${filters.examSessionId}/subjects`).then(res => {
      setSubjects(res.data.data || []);
      setFilters((prev: any) => ({ ...prev, subjectId: undefined }));
    }).finally(() => setLoadingFilter(false));
  }, [filters.examSessionId]);

  // Handler fetch báo cáo
  const handleFetchReport = async () => {
    setLoading(true);
    try {
      const result = await fetchExamScoreReport(filters);
      setData(result);
    } catch (e: any) {
      message.error(e.message || 'Lỗi tải báo cáo');
    }
    setLoading(false);
  };

  // Handler thay đổi filter
  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
  };

  // Handler xuất Excel
  const handleExportExcel = async () => {
    try {
      const blob = await exportExamScoreReport(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BaoCaoDiemKyThi.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success('Đã xuất file Excel!');
    } catch (e: any) {
      message.error(e.message || 'Xuất Excel thất bại');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2>Báo cáo kết quả chấm điểm kỳ thi</h2>
      {/* Filter section */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
        <Select
          placeholder="Chọn kỳ thi"
          style={{ minWidth: 200 }}
          onChange={v => handleFilterChange('examSessionId', v)}
          options={examSessions.map(item => ({ value: item.ExamSessionID, label: item.TenKyThi }))}
          allowClear
          loading={loadingFilter}
          value={filters.examSessionId}
        />
        <Select
          placeholder="Chọn lớp"
          style={{ minWidth: 200 }}
          onChange={v => handleFilterChange('classId', v)}
          options={classes.map(item => ({ value: item.LopHocID || item.LopHocPhanID, label: item.TenLop || item.TenLopHP }))}
          allowClear
          loading={loadingFilter}
          value={filters.classId}
          disabled={!filters.examSessionId}
        />
        <Select
          placeholder="Chọn môn học"
          style={{ minWidth: 200 }}
          onChange={v => handleFilterChange('subjectId', v)}
          options={subjects.map(item => ({ value: item.MonHocID, label: item.TenMonHoc }))}
          allowClear
          loading={loadingFilter}
          value={filters.subjectId}
          disabled={!filters.examSessionId}
        />
        <Button icon={<ReloadOutlined />} type="primary" onClick={handleFetchReport}>Xem báo cáo</Button>
        <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>Xuất Excel</Button>
      </div>
      <Spin spinning={loading}>
        <Table
          rowKey={(record) => record.MaHS + (record.NguoiCham || '')}
          dataSource={data}
          columns={[
            { title: 'Mã SV', dataIndex: 'MaHS' },
            { title: 'Họ tên', dataIndex: 'Hoten' },
            { title: 'Lớp', dataIndex: 'TenLop' },
            { title: 'Kỳ thi', dataIndex: 'TenKyThi' },
            { title: 'Môn học', dataIndex: 'TenMonHoc' },
            { title: 'Người chấm', dataIndex: 'NguoiCham' },
            { title: 'Điểm tổng', dataIndex: 'DiemTong' },
            { title: 'Trạng thái', dataIndex: 'TrangThai' },
            { title: '', render: (_, row) => (
              <Button
                type="link"
                onClick={() => {
                  if (!filters.examSessionId || isNaN(Number(filters.examSessionId))) {
                    message.error('Vui lòng chọn kỳ thi hợp lệ!');
                    return;
                  }
                  setSelectedRow({ ...row, ExamSessionID: Number(filters.examSessionId), NguoiCham: row.NguoiCham });
                  setModalOpen(true);
                }}
              >
                Chi tiết điểm
              </Button>
            )}
          ]}
          bordered
        />
      </Spin>
      <ScoreDetailModal open={modalOpen} onClose={() => setModalOpen(false)} row={selectedRow} />
    </div>
  );
};

export default ExamScoreReportPage; 